import { useMutation, useQuery, useQueryCache } from 'react-query';
import React, { useCallback, useState } from 'react';
import { QueryKey } from 'api/queryKey';
import Column from 'antd/lib/table/Column';
import { Button, message, Space, Tooltip } from 'antd';
import {
  Patient,
  patientsInitialPageParams,
  PatientType,
} from 'models/patient';
import { useEnsureContext } from 'common/hooks';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { Header } from 'common/components/Header';
import { BusinessPartnerDoctorsManagementRoutes } from 'pages/BusinessPartnerManagement/BusinessPartnerDoctorsManagement/businessPartnerDoctorsManagementRoutes';
import { useRouteMatch } from 'react-router';
import { Link } from 'react-router-dom';
import { PatientsFilters } from 'pages/BusinessPartnerManagement/BusinessPartnerPatientsManagement/BusinessPartnerPatientsPage/PatientsFilters';
import { purge } from 'lib/ramda/purge';
import { DeletePatientButton } from 'pages/SuperAdminModule/PatientsPage/DeletePatientButton';
import { BusinessPartnerImportPatientsModal } from 'pages/BusinessPartnerManagement/BusinessPartnerPatientsManagement/shared/BusinessPartnerImportPatientsModal';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { FormattedMessage } from 'react-intl';
import { NumberedTable } from 'common/components/NumberedTable';
import { toSimpleDate } from 'common/dateFnsUtils';
import { commonPaginationConfig } from 'models/page';
import { makeSubpartnersIdsString } from 'pages/BusinessPartnerManagement/BusinessPartnerMedicalOperatorsManagement/BusinessPartnerMedicalOperatorsPage/utils/makeSubpartnersIDsString';
import { useRole } from 'common/hooks/useRole';
import { UserRole } from 'models/token';
import { isNil } from 'ramda';

// Minimalny kształt rekordu w kolumnie "assigned_to"
type AssignedUser = { id?: number; firstName?: string; lastName?: string };

export const BusinessPartnerPatientsPage = () => {
  const bpContext = useEnsureContext(BusinessPartnerContext);
  const queryCache = useQueryCache();
  const { url } = useRouteMatch();
  const role = useRole();
  const { isMedicalOperator } = useRole();
  const shouldHidePartnerColumn =
    !isNil(bpContext.parentPartnerId) || isMedicalOperator;
  const [pageMetaData, setPageMetaData] = useState(patientsInitialPageParams);
  const [filters, setFilters] = useState({});
  const [filteredPartnerId, setFilteredPartnerId] = useState(0);
  const [importModalVisible, setImportModalVisible] = useState(false);

  const IDlist: number[] = [];
  const [IDsToGetData, setIDsToGetData] = useState('');

  const invalidatePatients = useCallback(
    () =>
      queryCache.invalidateQueries(QueryKey.BusinessPartnerPatientsList).then(),
    [queryCache]
  );

  const {
    isLoading: isLoadingSubpartners,
    isSuccess: isSubpartnersSuccess,
  } = useQuery(
    [QueryKey.BusinessPartnersList],
    () => BusinessPartnersAPI.fetchSubpartners(bpContext.id),
    {
      onSuccess: (res) => {
        const IDsString = makeSubpartnersIdsString(res, IDlist);
        setIDsToGetData(IDsString);
        invalidatePatients();
      },
      keepPreviousData: true,
    }
  );

  const { isFetching, data } = useQuery(
    [QueryKey.BusinessPartnerPatientsList, pageMetaData, filters, IDsToGetData],
    () =>
      BusinessPartnersAPI.fetchPatients(
        filteredPartnerId !== 0 ? filteredPartnerId : bpContext.id,
        {
          subPartnersIds: filteredPartnerId !== 0 ? '' : IDsToGetData,
          ...pageMetaData,
          ...filters,
          patientType: isMedicalOperator ? PatientType.Pro : undefined,
        }
      ),
    {
      enabled: isSubpartnersSuccess,
      keepPreviousData: true,
    }
  );

  const [mutate, { isLoading }] = useMutation(
    BusinessPartnersAPI.importPatientsFromFile
  );

  const intlMessage = useFormatMessage();

  const paginationConfig = {
    ...commonPaginationConfig,
    current: pageMetaData?.page,
    pageSize: data?.size,
    total: data?.totalElements,
    onChange: (page: number, pageSize?: number) =>
      setPageMetaData((prev) => ({
        ...prev,
        page,
        ...(pageSize && { pageSize }),
      })),
  };

  const importPatientsRequest = (file: File | Blob) => {
    mutate(
      {
        businessPartnerId: bpContext.id,
        dto: { file },
      },
      {
        onSuccess: (res) => {
          queryCache
            .invalidateQueries(QueryKey.BusinessPartnerPatientsList)
            .then();
          message.success(
            `${res.length} ${
              res.length === 1
                ? intlMessage({ id: 'medicalOperators.importPatient.success' })
                : intlMessage({ id: 'medicalOperators.importPatients.success' })
            }`
          );
          setImportModalVisible(false);
        },
      }
    );
  };

  return (
    <>
      <Header
        title={intlMessage({ id: 'patients' })}
        extra={[
          <Button
            id="button-import-patients-page"
            key="import"
            type="primary"
            onClick={() => setImportModalVisible(true)}
          >
            <FormattedMessage id="patients.import" />
          </Button>,

          <Link
            id="button-add-new-patient"
            className="ant-btn ant-btn-primary"
            key={`${url}/${BusinessPartnerDoctorsManagementRoutes.Register}`}
            to={`${url}/${BusinessPartnerDoctorsManagementRoutes.Register}`}
          >
            <FormattedMessage id="patients.addNew" />
          </Link>,
        ]}
      />
      <BusinessPartnerImportPatientsModal
        importRequest={importPatientsRequest}
        loading={isLoading}
        visible={importModalVisible}
        hideModal={() => setImportModalVisible(false)}
      />

      <PatientsFilters
        shouldHidePartnerColumn={shouldHidePartnerColumn}
        style={{ marginBottom: '1rem' }}
        onSubmit={(values) => {
          (values.partnerId === 0 || !values.partnerId) &&
            setFilteredPartnerId(0);
          values.partnerId && setFilteredPartnerId(values.partnerId);

          setPageMetaData((pageMetaData) => ({ ...pageMetaData, page: 1 }));
          setFilters(purge(values));
        }}
      />
      <NumberedTable
        rowKey="id"
        loading={isLoadingSubpartners || isFetching}
        dataSource={data?.content}
        pagination={paginationConfig}
        scroll={{ x: 'auto' }}
      >
        {!isLoadingSubpartners && !isFetching && (
          <>
            <Column
              title={intlMessage({ id: 'name' })}
              dataIndex="firstName"
              width={'40%'}
              render={(value, record: Patient) => (
                <Link key={`${url}/${record.id}`} to={`${url}/${record.id}`}>
                  {record.firstName + ' ' + record.lastName}
                </Link>
              )}
            />
            {!shouldHidePartnerColumn && (
              <Column
                title={intlMessage({ id: 'partner' })}
                dataIndex={['partnerDetails', 'name']}
              />
            )}
            <Column
              title={intlMessage({ id: 'create_date' })}
              dataIndex="createDate"
              render={(value) => toSimpleDate(value)}
            />

            <Column
              title={intlMessage({ id: 'assigned_to' })}
              key="assignedUsers"
              dataIndex="assignedUsers"
              render={(
                _,
                record: Patient & {
                  assignedUsers?: AssignedUser[];
                  assignedMedicalOperator?: {
                    id?: number;
                    firstName?: string;
                    lastName?: string;
                  } | null;
                  assignedFamilyUser?: {
                    id?: number;
                    firstName?: string;
                    lastName?: string;
                  } | null;
                }
              ) => {
                const fromArray: AssignedUser[] = Array.isArray(
                  record.assignedUsers
                )
                  ? record.assignedUsers
                  : [];

                type MaybeLegacy =
                  | { id?: number; firstName?: string; lastName?: string }
                  | null
                  | undefined;

                const legacyRaw: MaybeLegacy[] =
                  fromArray.length === 0
                    ? [
                        record.assignedMedicalOperator,
                        record.assignedFamilyUser,
                      ]
                    : [];

                const legacy: AssignedUser[] = legacyRaw.reduce<AssignedUser[]>(
                  (acc, u) => {
                    if (u)
                      acc.push({
                        id: u.id,
                        firstName: u.firstName,
                        lastName: u.lastName,
                      });
                    return acc;
                  },
                  []
                );

                const users: AssignedUser[] =
                  fromArray.length > 0 ? fromArray : legacy;

                if (users.length === 0) return <span>-</span>;

                const fmt = (u: AssignedUser) =>
                  [u.firstName, u.lastName].filter(Boolean).join(' ') || '-';

                const first = users[0];
                const rest = users.slice(1);

                if (rest.length === 0) {
                  return (
                    <span style={{ whiteSpace: 'normal' }}>{fmt(first)}</span>
                  );
                }

                const restNames = rest.map(fmt);
                const count = restNames.length;

                return (
                  <span style={{ whiteSpace: 'normal' }}>
                    {fmt(first)}{' '}
                    <Tooltip
                      title={
                        <div
                          style={{
                            maxWidth: 360,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          }}
                        >
                          {restNames.join(', ')}
                        </div>
                      }
                    >
                      <span
                        style={{
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                        onClick={(e) => e.preventDefault()}
                      >
                        + {count} more
                      </span>
                    </Tooltip>
                  </span>
                );
              }}
            />

            {role.role !== UserRole.MedicalOperator && (
              <Column
                title={intlMessage({ id: 'actions' })}
                key="actions"
                align="right"
                render={(value, record: Patient) => (
                  <Space direction={'vertical'} style={{ width: '150px' }}>
                    <DeletePatientButton
                      danger
                      patient={record}
                      onDeleted={invalidatePatients}
                    />
                  </Space>
                )}
              />
            )}
          </>
        )}
      </NumberedTable>
    </>
  );
};
